import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Zap, Save, RefreshCw, ChevronDown, ChevronUp, Info, Eye } from "lucide-react";
import StageEmailPreviewModal from "./StageEmailPreviewModal";
import RichEmailEditor from "./RichEmailEditor";

/**
 * Stage → auto-email rules editor. Sits in Settings → Email tab under
 * the delivery log. One row per Kanban stage; each row can be
 * independently enabled/disabled and edited.
 *
 * Rules are DORMANT by default — a fresh install fires zero
 * automations. Admin opts in per-stage as they're ready.
 *
 * Template tokens supported (rendered server-side in stage_email.py):
 *   {{business_name}}  {{contact_name}}  {{email}}  {{phone}}
 *   {{plan_name}}      {{service_address}}   {{avc_number}}
 *   {{stage_from}}     {{stage_to}}
 *   {{brand}}          {{partner_name}}   {{partner_code}}
 */

const TOKENS = [
  "business_name", "contact_name", "email", "phone", "plan_name",
  "service_address", "avc_number", "stage_from", "stage_to", "brand",
  "partner_name", "partner_code",
];

// Stages keyed by canonical backend order — mirrors DEFAULT_STAGES.
const STAGES = [
  { key: "not_contacted",  label: "Not contacted" },
  { key: "contacted",      label: "Contacted" },
  { key: "info_sent",      label: "Info sent" },
  { key: "form_submitted", label: "Form submitted" },
  { key: "provisioning",   label: "Provisioning" },
  { key: "installed",      label: "Installed" },
  { key: "live",           label: "Live" },
  { key: "completed",      label: "Completed" },
  { key: "lost",           label: "Lost or declined" },
];

function StageRuleRow({ stage, rule, onSave }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [draft, setDraft] = useState(() => ({
    enabled: rule?.enabled || false,
    send_to: rule?.send_to || "customer",
    subject_template: rule?.subject_template || "",
    body_html_template: rule?.body_html_template || "",
  }));

  useEffect(() => {
    // When the underlying rule changes (e.g. after a fresh reload),
    // reset the draft state so we're not editing stale data.
    setDraft({
      enabled: rule?.enabled || false,
      send_to: rule?.send_to || "customer",
      subject_template: rule?.subject_template || "",
      body_html_template: rule?.body_html_template || "",
    });
  }, [rule]);

  async function save(patch = {}) {
    setBusy(true);
    try {
      const merged = { ...draft, ...patch };
      await api.put(`/commissions/stage-email-rules/${stage.key}`, merged);
      toast.success(`${stage.label} — rule saved`);
      setDraft(merged);
      onSave();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  }

  async function toggleEnabled() {
    await save({ enabled: !draft.enabled });
  }

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/40 overflow-hidden" data-testid={`stage-rule-${stage.key}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-slate-400 hover:text-cyan-300"
          data-testid={`stage-rule-toggle-${stage.key}`}
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-slate-100 font-medium">{stage.label}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {draft.subject_template
              ? <>Subject: <span className="text-slate-400 font-mono">{draft.subject_template.slice(0, 70)}{draft.subject_template.length > 70 ? "…" : ""}</span></>
              : <span className="italic">No template yet</span>}
          </div>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            {draft.enabled ? "On" : "Off"}
          </span>
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={toggleEnabled}
            disabled={busy || (!draft.subject_template && !draft.enabled)}
            data-testid={`stage-rule-enable-${stage.key}`}
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-slate-800 peer-checked:bg-emerald-500 rounded-full relative transition-colors peer-disabled:opacity-40">
            <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-slate-100 rounded-full transition-transform ${draft.enabled ? "translate-x-4" : ""}`} />
          </div>
        </label>
      </div>

      {expanded && (
        <div className="border-t border-slate-800 bg-slate-950 px-4 py-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Send to</label>
              <select
                value={draft.send_to}
                onChange={(e) => setDraft((d) => ({ ...d, send_to: e.target.value }))}
                data-testid={`stage-rule-recipient-${stage.key}`}
                className="w-full bg-slate-900 border border-slate-800 rounded-lg text-sm px-2 py-1.5"
              >
                <option value="customer">Customer only</option>
                <option value="partner">Partner only</option>
                <option value="both">Both customer and partner</option>
              </select>
            </div>
            <div className="text-[10px] text-slate-500 flex items-start gap-1.5 md:col-span-1">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              <span>Rule fires when a customer <b>lands on this stage</b>. Test it by dragging a test card onto the column.</span>
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Subject template</label>
            <input
              type="text"
              value={draft.subject_template}
              onChange={(e) => setDraft((d) => ({ ...d, subject_template: e.target.value }))}
              placeholder="Your {{plan_name}} order is now in {{stage_to}}"
              data-testid={`stage-rule-subject-${stage.key}`}
              className="w-full bg-slate-900 border border-slate-800 rounded-lg text-sm px-2 py-1.5 font-mono"
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Email body</label>
            <RichEmailEditor
              value={draft.body_html_template}
              onChange={(html) => setDraft((d) => ({ ...d, body_html_template: html }))}
              tokens={TOKENS}
              placeholder="Hi {{contact_name}}, your {{plan_name}} order has moved to {{stage_to}}..."
              testid={`stage-rule-body-${stage.key}`}
            />
            <p className="text-[10px] text-slate-500 mt-1.5">
              Format with the toolbar. Click a token to drop <code className="text-slate-400 font-mono">&#123;&#123;placeholder&#125;&#125;</code> at the cursor — the sender fills them in.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              disabled={busy || (!draft.subject_template && !draft.body_html_template)}
              data-testid={`stage-rule-preview-${stage.key}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-700 hover:border-cyan-400 text-slate-200 hover:text-cyan-300 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              title="Render with sample data"
            >
              <Eye className="w-3.5 h-3.5" /> Preview email
            </button>
            <button
              type="button"
              onClick={() => save()}
              disabled={busy}
              data-testid={`stage-rule-save-${stage.key}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-semibold disabled:opacity-60"
            >
              <Save className="w-3.5 h-3.5" /> Save template
            </button>
          </div>
        </div>
      )}
      <StageEmailPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        stageKey={stage.key}
        stageLabel={stage.label}
        draft={{
          subject_template: draft.subject_template,
          body_html_template: draft.body_html_template,
        }}
      />
    </div>
  );
}

export default function StageEmailAutomations() {
  const [rules, setRules] = useState(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    setBusy(true);
    try {
      const { data } = await api.get("/commissions/stage-email-rules");
      setRules(data.rules || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load rules");
    } finally { setBusy(false); }
  }
  useEffect(() => { reload(); }, []);

  const byStage = useMemo(() => {
    const m = new Map();
    (rules || []).forEach((r) => m.set(r.stage_key, r));
    return m;
  }, [rules]);

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900 p-5" data-testid="stage-email-automations">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-slate-100 text-lg font-semibold inline-flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-300" /> Stage automations
          </h2>
          <p className="text-xs text-slate-400 mt-1 max-w-2xl">
            Fire an email automatically when a customer moves to a specific Kanban stage. Each rule is off until you flip it on, so nothing sends until you&apos;re ready. Customer, partner, or both — with token-interpolated subject &amp; body. Sends land in the delivery log above.
          </p>
        </div>
        <button
          type="button"
          onClick={reload}
          disabled={busy}
          data-testid="stage-rules-refresh"
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-cyan-300 px-2 py-1 rounded border border-slate-800 hover:border-slate-700"
        >
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="space-y-2">
        {STAGES.map((s) => (
          <StageRuleRow
            key={s.key}
            stage={s}
            rule={byStage.get(s.key)}
            onSave={reload}
          />
        ))}
      </div>
    </section>
  );
}
